import { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../../services/auth.service";
import { tagMonitorRequest, getCachedResponse, loadRequest } from "./homeRequests";

/**
 * Fetches one chart type of the Tag Monitor dashboard for a period
 *
 * @param {string} chartType - Chart type of the dashboard endpoint ("errors", "tag-anomaly" or "tag-inactivity")
 * @param {object} period - First day, last day and window of the request, see getPeriod on the homepage
 * @param {boolean} enabled - Only fetch when true
 * @param {string} scope - Part of the page that uses the data, for the error log
 * @param {string|null} granularity - "summary" for the homepage totals, null for the full rows of the dashboard
 * @returns {object|null|undefined} Response context, undefined while loading or disabled and null when the
 *   request failed
 */
export const useTagMonitorData = (chartType, period, enabled = true, scope = "Home", granularity = "summary") => {
  const { companyId } = useContext(AuthContext);
  const [result, setResult] = useState();

  const request = useMemo(
    () => tagMonitorRequest({ companyId, chartType, period, granularity }),
    [companyId, chartType, period, granularity],
  );

  // Only the response of this request (company and period), from the cache when it was loaded in the last minutes
  const cached = enabled ? getCachedResponse(request.key) : undefined;

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    loadRequest(request)
      .then((context) => {
        if (!cancelled) setResult({ key: request.key, context });
      })
      .catch((error) => {
        console.error(`[company ${companyId}] [${scope}] Failed to load the Tag Monitor ${chartType} data:`, error);
        if (!cancelled) setResult({ key: request.key, context: null });
      });

    // Ignore a response that arrives after the company or the period changed
    return () => {
      cancelled = true;
    };
  }, [request, enabled, companyId, scope, chartType]);

  if (!enabled) return undefined;
  return result?.key === request.key ? result.context : cached;
};
